import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import ProductList from "@/pages/ProductList";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Wishlist from "@/pages/Wishlist";
import Profile from "@/pages/account/Profile";
import MyOrders from "@/pages/account/MyOrders";
import OrderDetail from "@/pages/account/OrderDetail";
import Addresses from "@/pages/account/Addresses";
import Settings from "@/pages/account/Settings";
import About from "@/pages/About";
import Help from "@/pages/Help";
import NotFound from "@/pages/NotFound";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductEdit from "@/pages/admin/AdminProductEdit";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPickup from "@/pages/admin/AdminPickup";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminSpins from "@/pages/admin/AdminSpins";
import AdminTryOnSettings from "@/pages/admin/AdminTryOnSettings";

import Spin from "@/pages/Spin";

import api from "@/lib/api";

function AuthCallback() {
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/"); return; }
    const sid = m[1];
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        if (data.token) localStorage.setItem("sf_token", data.token);
        await refreshMe();
        window.history.replaceState({}, "", "/");
        nav("/");
      } catch {
        nav("/login");
      }
    })();
  }, [nav, refreshMe]);
  return <div className="min-h-[60vh] grid place-items-center text-slate-500">Signing you in…</div>;
}

function Shell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const isAdmin = location.pathname.startsWith("/admin") && location.pathname !== "/admin/login";
  const hideChrome = isAdmin;
  return (
    <div className="App">
      {!hideChrome && <Header />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<ProductList />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/order-success/:id" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/about" element={<About />} />
        <Route path="/help" element={<Help />} />
        <Route path="/spin" element={<Spin />} />

        <Route path="/account/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/account/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
        <Route path="/account/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
        <Route path="/account/addresses" element={<ProtectedRoute><Addresses /></ProtectedRoute>} />
        <Route path="/account/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/new" element={<AdminProductEdit />} />
          <Route path="products/:id" element={<AdminProductEdit />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="pickup" element={<AdminPickup />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="spins" element={<AdminSpins />} />
          <Route path="tryon" element={<AdminTryOnSettings />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      {!hideChrome && <Footer />}
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}
